// ============================================================================
// Lifecycle engine — pure validators over the canonical TRANSITIONS table.
// ============================================================================
// Shared by the frontend (to render only legal actions + collect missing
// fields) and mirrored in spirit by the entity-action Edge Function (which
// re-implements the same checks against its own copy of TRANSITIONS).
// ============================================================================

import { TRANSITIONS, type LifecycleEntity, type Transition } from './transitions';

/** All transitions legal from a given state (exact `from` + wildcard `*`). */
export function getTransitions(entity: LifecycleEntity, from: string): Transition[] {
  return TRANSITIONS[entity].filter((t) => t.from === from || t.from === '*');
}

/** The matching transition for from→to, preferring an exact `from` over `*`. Null if none. */
export function findTransition(
  entity: LifecycleEntity,
  from: string,
  to: string,
): Transition | null {
  const exact = TRANSITIONS[entity].find((t) => t.from === from && t.to === to);
  if (exact) return exact;
  return TRANSITIONS[entity].find((t) => t.from === '*' && t.to === to) ?? null;
}

export function isValidTransition(entity: LifecycleEntity, from: string, to: string): boolean {
  return findTransition(entity, from, to) !== null;
}

/** Status values reachable from `from`. */
export function getValidTargets(entity: LifecycleEntity, from: string): string[] {
  return getTransitions(entity, from).map((t) => t.to);
}

/** Required field names not satisfied by `context` (missing / null / blank). */
export function missingRequires(
  entity: LifecycleEntity,
  from: string,
  to: string,
  context: Record<string, unknown>,
): string[] {
  const transition = findTransition(entity, from, to);
  if (!transition?.requires) return [];
  return transition.requires.filter((field) => {
    const value = context[field];
    return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
  });
}
